import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Banknote, Building2, CheckCircle, Copy, CreditCard, Download, Eye, FileImage, Gift, MapPin, Package, RefreshCw, RotateCcw, Smartphone, Star, Truck, Upload, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, resolveMediaUrl, PLACEHOLDER_IMAGE } from "@/api";
import { filterRank } from "@/lib/adminSearch";

/* ── Load Razorpay script on demand ── */
async function loadRazorpay() {
  if (window.Razorpay) return window.Razorpay;
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(window.Razorpay);
    s.onerror = () => reject(new Error("Razorpay SDK load nahi hua. Internet check karo."));
    document.head.appendChild(s);
  });
}

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;
const stages = ["confirmed", "packed", "shipped", "delivered"];
const label = (v = "") => v.replaceAll("_", " ").replace(/\b\w/g, (l) => l.toUpperCase());
const RETRY_STATUSES = new Set(["payment_failed", "payment_pending"]);

/* ── Delivery Retention Modal ── */
function DeliveryRetentionModal({ order, onClose, onShowRate }) {
  const COUPON = "THANKS100";
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(COUPON).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success("Coupon code copy ho gaya!");
  };

  return (
    <div className="retention-overlay" data-testid="delivery-retention-modal">
      <div className="retention-modal">
        <div className="retention-confetti">
          {[...Array(14)].map((_, i) => (
            <span key={i} className="confetti-dot" style={{ animationDelay: `${i * 0.12}s`, left: `${6 + i * 6.5}%` }} />
          ))}
        </div>
        <button className="retention-close" onClick={onClose} data-testid="retention-close-btn"><X size={16} /></button>
        <div className="retention-icon-wrap">
          <div className="retention-icon"><Gift size={28} /></div>
        </div>
        <h3 className="retention-title">Order Deliver Ho Gaya!</h3>
        <p className="retention-sub">Bahut shukriya! Aapka <b>{order.items?.[0]?.name}</b> successfully deliver ho gaya.</p>
        <div className="retention-coupon" data-testid="retention-coupon-wrap">
          <span className="coupon-tag">Your Reward</span>
          <div className="coupon-code-box" onClick={copy} data-testid="copy-coupon-btn">
            <b className="coupon-text">{COUPON}</b>
            <span className="coupon-copy-hint">
              {copied ? <><CheckCircle size={13} /> Copied!</> : <><Copy size={13} /> Tap to copy</>}
            </span>
          </div>
          <small className="coupon-desc">₹100 off on next order above ₹2000</small>
        </div>
        <div className="retention-actions">
          <button className="retry-pay-btn" style={{ flex: 1 }} onClick={() => { onClose(); onShowRate(); }} data-testid="retention-rate-btn">
            <Star size={14} /> Rate Experience
          </button>
          <button className="order-secondary-button" onClick={onClose} data-testid="retention-skip-btn" style={{ flex: 1 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Rate Your Order Modal ── */
function RateOrderModal({ order, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (text.trim().length < 10) { toast.error("Review mein kam se kam 10 characters likhein"); return; }
    setBusy(true);
    try {
      await api.post(`/api/orders/${order.id}/review`, { rating, text: text.trim() });
      toast.success("Review submit ho gaya! Admin verify ke baad dikhega.");
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="retry-modal-overlay" data-testid="rate-order-modal">
      <div className="retry-modal" style={{ maxWidth: 460, width: "90%" }}>
        <div className="retry-modal-head">
          <div>
            <span className="eyebrow" style={{ color: "#f59e0b" }}>YOUR EXPERIENCE</span>
            <h3>Order Rate Karo</h3>
            <p style={{ fontSize: 12, color: "#6b7f9e", margin: "4px 0 0" }}>{order.items?.[0]?.name}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7f9e" }}><X size={18} /></button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "#8899b0", marginBottom: 8 }}>Rating do:</p>
          <div style={{ display: "flex", gap: 6, cursor: "pointer" }}>
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                size={32}
                fill={(hover || rating) >= s ? "#f59e0b" : "none"}
                color={(hover || rating) >= s ? "#f59e0b" : "#3a5070"}
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(s)}
                data-testid={`star-${s}`}
              />
            ))}
          </div>
          <p style={{ fontSize: 12, color: "#f59e0b", marginTop: 4 }}>
            {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
          </p>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: "#8899b0", marginBottom: 6 }}>Apna experience likhein:</p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Phone ki condition, delivery speed, overall experience... (min 10 characters)"
            rows={4}
            data-testid="review-text-area"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #1e3050", background: "#0d1f35", color: "#e0e8f0", fontSize: 13, resize: "vertical" }}
          />
          <small style={{ color: text.length < 10 ? "#ef4444" : "#22c55e", fontSize: 11 }}>{text.length} / 500 characters {text.length < 10 ? "(min 10)" : "✓"}</small>
        </div>
        <button
          className="retry-pay-btn full-width"
          onClick={submit}
          disabled={busy || text.trim().length < 10}
          data-testid="submit-rating-button"
        >
          {busy ? <RefreshCw size={14} className="spin-slow" /> : <Star size={14} />}
          {busy ? "Submitting…" : "Review Submit Karo"}
        </button>
      </div>
    </div>
  );
}

const PAYMENT_METHOD_OPTS = [
  { value: "upi",          label: "UPI / QR",         Icon: Smartphone,  detail: "Pay via any UPI app" },
  { value: "card",         label: "Credit / Debit Card", Icon: CreditCard, detail: "All major cards accepted" },
  { value: "net_banking",  label: "Net Banking",       Icon: Building2,   detail: "All major banks" },
  { value: "wallet",       label: "Store Wallet",      Icon: Wallet,      detail: "Instant — deducted from balance" },
  { value: "bank_transfer",label: "UPI Transfer (Manual)", Icon: Banknote, detail: "Pay & upload screenshot" },
];

export function OrderStatus({ status }) {
  return <span className={`order-status status-${status}`} data-testid={`order-status-${status}`}>{label(status)}</span>;
}

function BillButton({ id }) {
  const dl = async () => {
    try {
      const res = await api.get(`/api/orders/${id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `DMobileMart-${id}-invoice.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Invoice download started");
    } catch (e) { toast.error(apiError(e)); }
  };
  return <button className="order-secondary-button" onClick={dl} data-testid={`download-bill-${id}`}><Download size={15} /> Bill</button>;
}

/* ── Payment Proof Upload (for bank_transfer / manual UPI orders) ── */
function ProofUploadForm({ order, onChanged }) {
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const submit = async () => {
    if (!ref.trim()) { toast.error("UTR / Reference number daalo"); return; }
    if (!file) { toast.error("Payment screenshot upload karo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("payment_reference", ref.trim().toUpperCase());
      fd.append("file", file);
      await api.post(`/api/payments/manual-proof?order_id=${order.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment proof submit ho gayi! Admin verify karega.");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="proof-upload-form" data-testid="proof-upload-form">
      <div className="proof-upload-header">
        <FileImage size={16} style={{ color: "#00d4ff" }} />
        <span>Payment Screenshot Upload Karo</span>
      </div>
      <label className="proof-field">
        <span>UTR / Transaction Reference *</span>
        <input value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g. UTR123456789012" data-testid="utr-reference-input" />
      </label>
      <div className="proof-file-slot" onClick={() => fileRef.current?.click()} data-testid="proof-file-slot">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={e => setFile(e.target.files[0])} />
        {file
          ? <><Upload size={16} color="#22c55e" /><span style={{ color: "#22c55e" }}>{file.name}</span></>
          : <><Upload size={16} /><span>Screenshot click karo ya drag karo (JPG/PNG max 5MB)</span></>
        }
      </div>
      <button className="retry-pay-btn" onClick={submit} disabled={busy || !ref.trim() || !file} data-testid="submit-proof-btn" style={{ marginTop: 8 }}>
        {busy ? <RefreshCw size={14} className="spin-slow" /> : <Upload size={14} />} {busy ? "Uploading…" : "Submit Proof"}
      </button>
    </div>
  );
}

/* ── Retry Payment with Method Choice ── */
function RetryPaymentModal({ order, payConfig, onClose, onSuccess }) {
  const [method, setMethod] = useState(order.payment?.method || "upi");
  const [busy, setBusy] = useState(false);

  const markFailed = async (orderId) => {
    try { await api.post("/api/payments/razorpay/fail", { order_id: orderId, reason: "checkout_dismissed" }); } catch (_) {}
  };

  const proceed = async () => {
    setBusy(true);
    try {
      // Step 1: Reset order to retryable state
      const { data: retried } = await api.post(`/api/orders/${order.id}/retry-payment`, { new_payment_method: method });

      // Step 2: Wallet payment
      if (method === "wallet") {
        const { data: paid } = await api.post(`/api/orders/${retried.id}/pay-wallet`);
        toast.success("Wallet se payment ho gayi! Order confirmed!");
        onSuccess(paid); onClose(); return;
      }

      // Step 3: Manual bank transfer
      if (method === "bank_transfer") {
        toast.info("UPI QR order ke andar dikhega. Screenshot upload karo.");
        onSuccess(retried); onClose(); return;
      }

      // Step 4: Razorpay — load script dynamically
      const RazorpayCls = await loadRazorpay();
      if (!RazorpayCls) throw new Error("Payment gateway load nahi hua.");

      const { data: payment } = await api.post("/api/payments/razorpay/order", { order_id: retried.id });
      const succeeded = { value: false };

      const checkout = new RazorpayCls({
        key: payment.key_id,
        amount: payment.amount,
        currency: payment.currency,
        order_id: payment.razorpay_order_id,
        name: "DMobileMart",
        description: `Order ${payment.order_number}`,
        prefill: { name: payment.name, email: payment.email },
        theme: { color: "#00d4ff" },
        method: {
          upi: method === "upi",
          card: method === "card",
          netbanking: method === "net_banking",
          wallet: false,
        },
        handler: async (response) => {
          try {
            succeeded.value = true;
            await api.post("/api/payments/razorpay/verify", {
              order_id: retried.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful! Order confirmed!");
            onSuccess(); onClose();
          } catch (e) {
            await markFailed(retried.id);
            toast.error(apiError(e));
          } finally { setBusy(false); }
        },
        modal: {
          ondismiss: async () => {
            if (!succeeded.value) { await markFailed(retried.id); toast.info("Payment band kar diya."); onSuccess(); }
            setBusy(false); onClose();
          },
        },
      });
      checkout.open();
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const available = PAYMENT_METHOD_OPTS.filter(opt => {
    if (!payConfig) return true;
    const map = { upi: "upi", card: "card", net_banking: "net_banking", wallet: "wallet", bank_transfer: "bank_transfer" };
    return payConfig.methods?.[map[opt.value]] !== false;
  });

  return (
    <div className="retry-modal-overlay" data-testid="retry-payment-modal">
      <div className="retry-modal">
        <div className="retry-modal-head">
          <div>
            <span className="eyebrow" style={{ color: "#f59e0b" }}>RETRY PAYMENT</span>
            <h3>Payment method chunao</h3>
            <p style={{ fontSize: 12, color: "#6b7f9e", margin: "4px 0 0" }}>Order {order.order_number} · {money(order.total)}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7f9e" }}><X size={18} /></button>
        </div>
        <div className="retry-method-list">
          {available.map(({ value, label: lbl, Icon, detail }) => (
            <label key={value} className={`retry-method-opt${method === value ? " selected" : ""}`} data-testid={`retry-method-${value}`}>
              <input type="radio" name="retry_method" value={value} checked={method === value} onChange={() => setMethod(value)} />
              <Icon size={18} />
              <div><b>{lbl}</b><small>{detail}</small></div>
            </label>
          ))}
        </div>
        <button className="retry-pay-btn full-width" onClick={proceed} disabled={busy} data-testid="proceed-retry-btn">
          {busy ? <RefreshCw size={14} className="spin-slow" /> : <RotateCcw size={14} />}
          {busy ? "Processing…" : `Pay ${money(order.total)} via ${available.find(o => o.value === method)?.label || method}`}
        </button>
      </div>
    </div>
  );
}

function UnpaidOrderActions({ order, onChanged, payConfig }) {
  const [showRetryModal, setShowRetryModal] = useState(false);

  const cancel = async () => {
    try { await api.post(`/api/orders/${order.id}/cancel`); toast.success("Order cancelled, stock restored"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };

  const canRetry = RETRY_STATUSES.has(order.status);
  const needsProof = order.status === "payment_proof_required";
  if (!canRetry && !needsProof) return null;

  return (
    <>
      {showRetryModal && <RetryPaymentModal order={order} payConfig={payConfig} onClose={() => setShowRetryModal(false)} onSuccess={() => { setShowRetryModal(false); onChanged(); }} />}
      <div className="unpaid-order-actions" data-testid={`unpaid-actions-${order.id}`}>
        {canRetry && (
          <button className="retry-pay-btn" onClick={() => setShowRetryModal(true)} data-testid={`retry-payment-${order.id}`}>
            <RotateCcw size={14} /> Retry Payment
          </button>
        )}
        {needsProof && <ProofUploadForm order={order} onChanged={onChanged} />}
        {!needsProof && <button className="order-secondary-button" onClick={cancel} data-testid={`cancel-order-${order.id}`}>Cancel</button>}
      </div>
    </>
  );
}

function Timeline({ order }) {
  const current = stages.indexOf(order.status);
  return (
    <div className="order-timeline" data-testid={`order-timeline-${order.id}`}>
      {stages.map((stage, i) => (
        <div className={i <= current ? "complete" : ""} key={stage}>
          <i>{i < current ? "✓" : i + 1}</i>
          <span><b>{label(stage)}</b><small>{order.tracking?.events?.find((e) => e.status?.toLowerCase() === label(stage).toLowerCase())?.at?.slice(0, 10) || (i > current ? "Pending" : "Updated")}</small></span>
        </div>
      ))}
    </div>
  );
}

export function CustomerOrders() {
  const [orders, setOrders] = useState([]); const [loading, setLoading] = useState(true); const [tab, setTab] = useState("all");
  const [payConfig, setPayConfig] = useState(null);
  const [rateOrder, setRateOrder] = useState(null);
  const load = useCallback(() => api.get("/api/orders?page_size=100").then(({ data }) => setOrders(data.items)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)), []);
  useEffect(() => { load(); api.get("/api/payment-config").then(({ data }) => setPayConfig(data)).catch(() => {}); }, [load]);
  const visible = orders.filter((o) => tab === "all" || (tab === "active" ? !["delivered", "cancelled"].includes(o.status) : o.status === tab));
  const unpaidCount = orders.filter((o) => RETRY_STATUSES.has(o.status)).length;

  return (
    <main className="simple-page order-page" data-testid="customer-orders-page">
      {rateOrder && <RateOrderModal order={rateOrder} onClose={() => setRateOrder(null)} onDone={load} />}
      <header className="order-page-head">
        <div>
          <span className="eyebrow">PURCHASE HISTORY</span>
          <h1>My Orders</h1>
          <p>Track every delivery, download invoice aur return manage karo.</p>
        </div>
        <Link to="/category" className="order-secondary-button" data-testid="continue-shopping-link">Shop more</Link>
      </header>

      <div className="order-tabs" data-testid="customer-order-tabs">
        {[["all", "All"], ["active", "In progress"], ["delivered", "Delivered"]].map(([v, t]) => (
          <button key={v} className={tab === v ? "active" : ""} onClick={() => setTab(v)} data-testid={`customer-order-tab-${v}`}>
            {t}{v === "active" && unpaidCount > 0 && <span className="tab-badge">{unpaidCount}</span>}
          </button>
        ))}
      </div>

      {loading ? <p className="order-empty">Orders load ho rahe hain…</p> : visible.length ? (
        <div className="customer-order-list">
          {visible.map((order) => (
            <article className="customer-order-card" key={order.id} data-testid={`customer-order-${order.id}`}>
              <img src={resolveMediaUrl(order.items?.[0]?.image) || PLACEHOLDER_IMAGE} alt="" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
              <div className="customer-order-main">
                <div><span className="order-number">{order.order_number}</span><OrderStatus status={order.status} /></div>
                <b>{order.items?.[0]?.name}{order.items?.length > 1 ? ` +${order.items.length - 1} more` : ""}</b>
                <small>{new Date(order.created_at).toLocaleDateString("en-IN")} · {money(order.total)}</small>
                {order.payment?.is_partial && <small className="partial-badge" data-testid={`partial-badge-${order.id}`}>{money(order.payment.amount_remaining)} remaining at delivery</small>}
              </div>
              <div className="customer-order-actions">
                <Link to={`/my-orders/${order.id}`} className="order-primary-button" data-testid={`view-order-${order.id}`}><Eye size={15} /> Track</Link>
                <BillButton id={order.id} />
                {order.status === "delivered" && (
                  <button
                    className="order-secondary-button"
                    onClick={() => setRateOrder(order)}
                    data-testid={`rate-order-${order.id}`}
                    style={{ color: "#f59e0b", borderColor: "#f59e0b44" }}
                  >
                    <Star size={14} /> Rate
                  </button>
                )}
                <UnpaidOrderActions order={order} onChanged={load} payConfig={payConfig} />
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="order-empty"><Package size={30} /><h3>Koi orders nahi hain abhi</h3><Link to="/category" className="order-primary-button" data-testid="empty-orders-shop-link">Shopping start karo</Link></div>
      )}
    </main>
  );
}

export function CustomerOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [payConfig, setPayConfig] = useState(null);
  const [showRate, setShowRate] = useState(false);
  const [showRetention, setShowRetention] = useState(false);
  const reload = () => api.get(`/api/orders/${id}`).then(({ data }) => setOrder(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { reload(); api.get("/api/payment-config").then(({ data }) => setPayConfig(data)).catch(() => {}); }, [id]);

  // Auto-show retention modal once per session for delivered orders
  useEffect(() => {
    if (order?.status === "delivered") {
      const key = `retention_${order.id}`;
      if (!sessionStorage.getItem(key)) {
        setTimeout(() => setShowRetention(true), 600);
        sessionStorage.setItem(key, "1");
      }
    }
  }, [order?.status, order?.id]);

  if (!order) return <main className="simple-page order-page"><p className="order-empty">Order load ho raha hai…</p></main>;
  const isPartial = order.payment?.is_partial;
  return (
    <main className="simple-page order-page" data-testid="customer-order-detail">
      {showRetention && (
        <DeliveryRetentionModal
          order={order}
          onClose={() => setShowRetention(false)}
          onShowRate={() => setShowRate(true)}
        />
      )}
      {showRate && <RateOrderModal order={order} onClose={() => setShowRate(false)} onDone={reload} />}
      <Link to="/my-orders" className="order-back" data-testid="back-to-orders-link">← My orders</Link>
      <header className="order-page-head">
        <div><span className="eyebrow">ORDER {order.order_number}</span><h1>{label(order.status)}</h1><p>Placed {new Date(order.created_at).toLocaleString("en-IN")}</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <BillButton id={order.id} />
          {order.status === "delivered" && (
            <button className="order-secondary-button" onClick={() => setShowRate(true)} data-testid="rate-delivered-order-btn" style={{ color: "#f59e0b", borderColor: "#f59e0b44" }}>
              <Star size={14} /> Rate Order
            </button>
          )}
        </div>
      </header>
      {(RETRY_STATUSES.has(order.status) || order.status === "payment_proof_required") && (
        <div className="order-retry-banner" data-testid="order-retry-banner">
          <span>{RETRY_STATUSES.has(order.status) ? "Payment complete nahi hua — retry karo ya cancel karo" : "UPI payment proof upload karo"}</span>
          <UnpaidOrderActions order={order} onChanged={reload} payConfig={payConfig} />
        </div>
      )}
      {order.status === "delivered" && (
        <div className="order-retry-banner" style={{ background: "#f59e0b11", borderColor: "#f59e0b33" }} data-testid="delivered-rate-banner">
          <Star size={16} color="#f59e0b" />
          <span>Order deliver ho gaya! Apna experience share karein.</span>
          <button className="retry-pay-btn" onClick={() => setShowRate(true)} data-testid="delivered-rate-btn" style={{ marginLeft: "auto" }}>
            <Star size={14} /> Rate & Review
          </button>
        </div>
      )}
      {isPartial && (
        <div className="partial-payment-banner" data-testid="partial-payment-banner">
          <AlertTriangle size={16} color="#f59e0b" />
          <div>
            <b>Partial Payment — {money(order.payment.amount_paid)} online paid</b>
            <span>{money(order.payment.amount_remaining)} remaining at delivery (Cash on Delivery)</span>
          </div>
        </div>
      )}
      <div className="order-detail-grid">
        <section className="order-detail-main">
          <section className="order-detail-card"><h2>Delivery progress</h2><Timeline order={order} />{order.tracking?.number && <p className="tracking-number"><Truck size={15} /> Tracking: <b>{order.tracking.number}</b></p>}</section>
          <section className="order-detail-card"><h2>Items</h2>{order.items.map((item) => <div className="order-item" key={`${item.product_id}-${item.variant_sku || ""}`}><img src={resolveMediaUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} /><div><b>{item.name}</b><small>{item.variant_sku || "Standard"} · Qty {item.quantity}</small></div><strong>{money(item.price * item.quantity)}</strong></div>)}</section>
        </section>
        <aside className="order-detail-side">
          <section className="order-detail-card">
            <h3>Payment summary</h3>
            <p><span>Subtotal</span><b>{money(order.subtotal)}</b></p>
            <p><span>Discount</span><b>−{money(order.discount)}</b></p>
            <p><span>Platform fee</span><b>{money(order.platform_fee)}</b></p>
            <hr />
            <p className="order-total"><span>Total</span><b>{money(order.total)}</b></p>
            {isPartial && <>
              <p style={{ color: "#22c55e", marginTop: 4 }}><span>Paid Online</span><b>{money(order.payment.amount_paid)}</b></p>
              <p style={{ color: "#f59e0b" }}><span>Remaining (COD)</span><b>{money(order.payment.amount_remaining)}</b></p>
            </>}
            <small>{label(order.payment?.method)} · {label(order.payment?.status)}</small>
          </section>
          <section className="order-detail-card"><h3>Delivery address</h3><p className="address-copy"><MapPin size={15} /><span><b>{order.delivery_address?.recipient_name}</b>{order.delivery_address?.line1}<br />{order.delivery_address?.city} — {order.delivery_address?.postal_code}</span></p></section>
          <Link to="/account#returns" className="order-secondary-button" data-testid="order-help-link"><RefreshCw size={15} /> Return / Help</Link>
        </aside>
      </div>
    </main>
  );
}

export function AdminOrderManager({ query = "" }) {
  const [orders, setOrders] = useState([]); const [selected, setSelected] = useState(null); const [status, setStatus] = useState("confirmed"); const [tracking, setTracking] = useState(""); const [reviewNote, setReviewNote] = useState("");
  const load = useCallback(() => api.get("/api/orders?page_size=100").then(({ data }) => setOrders(data.items)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const open = async (id) => { try { const { data } = await api.get(`/api/orders/${id}`); setSelected(data); setStatus(data.status); setTracking(data.tracking?.number || ""); } catch (e) { toast.error(apiError(e)); } };
  const save = async () => { try { const { data } = await api.patch(`/api/admin/orders/${selected.id}/status?status=${status}&tracking_number=${encodeURIComponent(tracking)}`); setSelected(data); load(); toast.success("Order status updated"); } catch (e) { toast.error(apiError(e)); } };
  const reviewPayment = async (action) => { try { const { data } = await api.post(`/api/admin/orders/${selected.id}/payment-review`, { action, note: reviewNote }); setSelected(data); setStatus(data.status); load(); toast.success(action === "capture" ? "Payment captured" : "Payment rejected"); } catch (e) { toast.error(apiError(e)); } };
  const visible = filterRank(orders, query, [(o) => o.order_number, (o) => o.items?.map((i) => i.name).join(" "), (o) => o.status]);
  return (
    <section className="admin-manager admin-orders" data-testid="admin-order-manager">
      <div className="admin-panel admin-order-table">
        <div className="panel-head"><div><h2>Order operations</h2><span>{visible.length} orders</span></div><RefreshCw size={17} /></div>
        <div className="table-panel">
          <table data-testid="admin-orders-table">
            <thead><tr><th>Order</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th>View</th></tr></thead>
            <tbody>{visible.map((o) => <tr key={o.id}><td><b>{o.order_number}</b><small>{new Date(o.created_at).toLocaleDateString("en-IN")}</small></td><td>{o.items?.[0]?.name}</td><td>{money(o.total)}</td><td>{label(o.payment?.method)}</td><td><OrderStatus status={o.status} /></td><td><button onClick={() => open(o.id)} data-testid={`admin-view-order-${o.id}`}>View</button></td></tr>)}</tbody>
          </table>
        </div>
      </div>
      {selected && (
        <aside className="admin-order-drawer" data-testid="admin-order-drawer">
          <div className="drawer-head"><div><span className="eyebrow">{selected.order_number}</span><h2>Fulfillment</h2></div><button onClick={() => setSelected(null)} data-testid="close-admin-order-drawer"><X size={18} /></button></div>
          <div className="drawer-items">{selected.items.map((item) => <p key={item.product_id}><b>{item.name}</b><span>{item.quantity} × {money(item.price)}</span></p>)}</div>
          {selected.payment?.method === "bank_transfer" && (
            <section className="payment-review-panel" data-testid="admin-payment-review-panel">
              <h3>UPI Payment Control</h3>
              {selected.payment?.payment_reference && <p data-testid="admin-payment-reference"><span>UTR</span><b>{selected.payment.payment_reference}</b></p>}
              {selected.payment?.proof_file_id ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${selected.id}/payment-proof`} alt="Proof" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} /> : <p>No proof uploaded.</p>}
              <label>Note<input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Review note" data-testid="admin-payment-review-note" /></label>
              {selected.payment?.status === "review_pending" && <div><button onClick={() => reviewPayment("capture")} data-testid="capture-payment-button">Verify & Confirm</button><button onClick={() => reviewPayment("reject")} data-testid="reject-payment-button">Reject</button></div>}
            </section>
          )}
          <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-order-status-select">{["confirmed", "packed", "shipped", "delivered", "cancelled"].map((v) => <option value={v} key={v}>{label(v)}</option>)}</select></label>
          <label>Tracking<input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="AWB / tracking ID" data-testid="admin-order-tracking-input" /></label>
          <div className="drawer-actions"><button className="primary-btn" onClick={save} data-testid="save-admin-order-button">Save</button><BillButton id={selected.id} /></div>
        </aside>
      )}
    </section>
  );
}
